import { productStorage, storageTransaction, writeWithReadback, type ProductStorage } from "./storage";

export const auxiliaryKeys = ["hs-mute", "hs-haptics", "hs-reduced-sensory", "hs-audio-volume", "hs-last-train-v2", "hs-saved-venues-v1", "hs-support-draft-v2", "hs-support-outbox-v1", "hs-support-draft"] as const;
type Status = "ready" | "pending" | "unavailable";

/** A hydrated synchronous view for audio/UI; persistence still waits for the channel ACK. */
export function createAuxiliaryStorage(resolveStorage: () => ProductStorage) {
  const values = new Map<string, string | null>();
  const unread = new Set<string>(auxiliaryKeys);
  const dirty = new Map<string, string | null>();
  const listeners = new Set<() => void>();
  let status: Status = "pending";
  let pending: Promise<void> = Promise.resolve();
  const publish = (next: Status) => { status = next; for (const listener of listeners) listener(); };
  const check = (key: string) => { if (!(auxiliaryKeys as readonly string[]).includes(key)) throw new Error("Unknown auxiliary storage key"); };
  const flush = () => {
    pending = pending.catch(() => undefined).then(async () => {
      const storage = resolveStorage();
      await storageTransaction(storage, async () => {
        for (const [key, value] of dirty) {
          if (unread.has(key)) continue;
          if (value === null) {
            await storage.removeItem(key);
            if (await storage.getItem(key) !== null) throw new Error("Delete ACK unknown");
          } else await writeWithReadback(storage, key, value);
          if (dirty.get(key) === value) dirty.delete(key);
        }
      });
      publish(unread.size ? "unavailable" : dirty.size ? "pending" : "ready");
      if (unread.size) throw new Error("Storage unavailable");
    }).catch((error) => { publish("unavailable"); throw error; });
    return pending;
  };
  const hydrate = async () => {
    publish("pending");
    await Promise.all([...unread].map(async (key) => {
      try {
        const value = await resolveStorage().getItem(key);
        if (value !== null && ["hs-last-train-v2", "hs-saved-venues-v1", "hs-support-draft-v2", "hs-support-outbox-v1"].includes(key)) {
          const parsed: unknown = JSON.parse(value);
          if (key === "hs-saved-venues-v1" ? !Array.isArray(parsed) : !parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Unreadable stored record");
        }
        if (!dirty.has(key)) values.set(key, value);
        unread.delete(key);
      } catch {
        // Unknown saved sensory preferences must not unexpectedly play sound or vibrate.
        if (!dirty.has(key) && ["hs-mute", "hs-reduced-sensory"].includes(key)) values.set(key, "on");
        if (!dirty.has(key) && key === "hs-haptics") values.set(key, "off");
      }
    }));
    await flush().catch(() => undefined);
  };
  const update = (key: string, value: string | null) => {
    check(key); values.set(key, value); dirty.set(key, value); publish("pending");
    void flush().catch(() => undefined);
  };
  return {
    getItem: (key: string) => { check(key); return values.get(key) ?? null; },
    setItem: (key: string, value: string) => update(key, value),
    removeItem: (key: string) => update(key, null),
    hydrate, flush,
    getStatus: () => status,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };
}

export const auxiliaryStorage = createAuxiliaryStorage(productStorage);
